/**
 * The persona create / clone / edit dialogs.
 *
 * These live apart from `AgentPersonasPage` because four modules outside this
 * one want a dialog and none of them want the page: `PersonaMentionCreate`,
 * `PersonaMentionEdit`, `AgentPersonaDetailPage`, and web's
 * `LaunchPersonaHeader`. While they imported the dialogs from the page module,
 * every one of those static imports dragged the 950-line page — and its whole
 * import graph — into the eager bundle, which defeated the `import()` in
 * `register.ts` and in `AgentsHubPage`. Rollup says so out loud:
 *
 *   (!) AgentPersonasPage.tsx is dynamically imported by register.ts,
 *       AgentsHubPage.tsx but also statically imported by
 *       AgentPersonaDetailPage.tsx, PersonaMentionCreate.tsx,
 *       PersonaMentionEdit.tsx, LaunchPersonaHeader.tsx — dynamic import will
 *       not move module into another chunk.
 *
 * Import dialogs from here, not from the page.
 */

import { X } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';

import { useAuth } from '@reflex/ui/client/context';
import { permissionErrorDescription } from '@reflex/ui/client/permissions';
import { defaultOwnerForUser, type ResourceOwnerInput } from '@reflex/shared';
import { useCreateAgentPersona, useUpdateAgentPersona } from './useAgentPersonas.js';
import { type AgentPersona } from '../shared/types.js';
import {
  PersonaForm,
  ownersDiffer,
  type PersonaConfigDefaults,
  type PersonaMetaFormValues,
  type PersonaSubmitPayload,
} from './persona-form.js';
import {
  buildPersonaCreatePayload,
  buildPersonaUpdatePayload,
  personaFormDefaults,
} from './persona-defaults.js';
import {
  PERSONA_FORM_DIALOG_CONTENT_CLASS,
  PERSONA_FORM_DIALOG_HEADER_CLASS,
} from './persona-form-layout.js';

export interface CreatePersonaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metaDefaults: PersonaMetaFormValues;
  configDefaults: PersonaConfigDefaults;
  title: string;
  description: string;
  successMessage: string;
  /**
   * Called with the created persona once the mutation succeeds, before the
   * dialog closes. Lets callers act on the new persona — e.g. the launch
   * `@`-mention "Add persona" surface applies it to the run being configured.
   */
  onCreated?: (created: AgentPersona) => void;
  /**
   * Optional action rendered on the success toast — "Save as persona" uses it
   * to offer a link to the persona that was just created, since its caller
   * (an agent view) is nowhere near the personas list.
   */
  successAction?: { label: string; onClick: () => void };
  /** Submit button label. Defaults to "Create persona". */
  submitLabel?: string;
}

export function CreatePersonaDialog({
  open,
  onOpenChange,
  metaDefaults,
  configDefaults,
  title,
  description,
  successMessage,
  onCreated,
  successAction,
  submitLabel = 'Create persona',
}: CreatePersonaDialogProps) {
  const createPersona = useCreateAgentPersona();

  function onSubmit(payload: PersonaSubmitPayload) {
    createPersona.mutate(buildPersonaCreatePayload(payload), {
      onSuccess: (created) => {
        onCreated?.(created);
        toast.success(successMessage, successAction ? { action: successAction } : undefined);
        onOpenChange(false);
      },
      onError: (err: Error) =>
        toast.error('Failed to create persona', {
          description: permissionErrorDescription(err),
        }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={PERSONA_FORM_DIALOG_CONTENT_CLASS} showCloseButton={false}>
        <DialogHeader className={PERSONA_FORM_DIALOG_HEADER_CLASS}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base">{title}</DialogTitle>
              <DialogDescription className="text-xs max-sm:hidden">{description}</DialogDescription>
            </div>
            <DialogClose
              aria-label="Close"
              className="shrink-0 rounded-xs p-1 text-muted-foreground transition-colors hover:text-foreground sm:hidden"
            >
              <X className="size-4" />
            </DialogClose>
          </div>
        </DialogHeader>
        {/* Deliberately ungated. `POST /agent-personas` runs under
            `personas:read`, and the create form submits the caller's own
            user id as the explicit owner, so any member who
            can see this page can create a persona they own. The write slug
            is required only to pick the Organization owner, which
            `PersonaForm`'s `OwnerField` gates on its own. */}
        <PersonaForm
          metaDefaults={metaDefaults}
          configDefaults={configDefaults}
          isPending={createPersona.isPending}
          onSubmit={onSubmit}
          submitLabel={submitLabel}
        />
      </DialogContent>
    </Dialog>
  );
}

interface ClonePersonaDialogProps {
  persona: AgentPersona;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClonePersonaDialog({ persona, open, onOpenChange }: ClonePersonaDialogProps) {
  const { user } = useAuth();
  const defaults = personaFormDefaults(persona);
  return (
    <CreatePersonaDialog
      open={open}
      onOpenChange={onOpenChange}
      metaDefaults={{
        ...defaults.meta,
        name: `${persona.name} (copy)`,
        // A duplicate is a create, so it starts under the caller — not
        // under the source's owner, which a member may not be allowed to
        // write and which `OwnerSelect` cannot even display for the
        // host tier.
        owner: defaultOwnerForUser(user?.id ?? ''),
      }}
      configDefaults={defaults.config}
      title="Duplicate persona"
      description="Review and tweak the fields copied from the original, then save it as a new persona."
      successMessage="Persona duplicated"
    />
  );
}

interface EditPersonaDialogProps {
  persona: AgentPersona;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the saved persona once the mutation succeeds, before the dialog
   * closes. Mirrors {@link CreatePersonaDialogProps.onCreated} — the launch
   * `@`-mention edit surface uses it to apply the persona to the run.
   */
  onUpdated?: (updated: AgentPersona) => void;
}

export function EditPersonaDialog({
  persona,
  open,
  onOpenChange,
  onUpdated,
}: EditPersonaDialogProps) {
  const updatePersona = useUpdateAgentPersona();
  const initialOwner: ResourceOwnerInput = { ownerId: persona.ownerId };
  const defaults = personaFormDefaults(persona);

  function onSubmit(payload: PersonaSubmitPayload) {
    const ownerChanged = ownersDiffer(payload.owner, initialOwner);
    updatePersona.mutate(
      {
        id: persona.id,
        data: {
          ...buildPersonaUpdatePayload(payload),
          ...(ownerChanged ? { owner: payload.owner } : {}),
        },
      },
      {
        onSuccess: (updated) => {
          onUpdated?.(updated);
          toast.success('Persona updated');
          onOpenChange(false);
        },
        onError: (err: Error) =>
          toast.error('Failed to update persona', {
            description: permissionErrorDescription(err),
          }),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={PERSONA_FORM_DIALOG_CONTENT_CLASS} showCloseButton={false}>
        <DialogHeader className={PERSONA_FORM_DIALOG_HEADER_CLASS}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base">Edit persona</DialogTitle>
              <DialogDescription className="text-xs max-sm:hidden">
                Update this persona&apos;s prompt, repo, blueprint, and attachments.
              </DialogDescription>
            </div>
            <DialogClose
              aria-label="Close"
              className="shrink-0 rounded-xs p-1 text-muted-foreground transition-colors hover:text-foreground sm:hidden"
            >
              <X className="size-4" />
            </DialogClose>
          </div>
        </DialogHeader>
        <PersonaForm
          metaDefaults={defaults.meta}
          configDefaults={defaults.config}
          isPending={updatePersona.isPending}
          onSubmit={onSubmit}
          submitLabel="Save changes"
        />
      </DialogContent>
    </Dialog>
  );
}
